import { apiFetch, getApiHost } from './api';
import { posthogCapture, captureError } from './posthog';

/**
 * Product analytics beacon. Each call writes a row to the API's
 * analytics_events Mongo collection (and mirrors to Loki). Server-side
 * snapshots req.user → userId/walletAddress; the client supplies tier
 * + dnetBalance + isAuthenticated via a context singleton (see
 * setAnalyticsContext) so funnel queries can slice by user state at
 * event time without a per-event tier-resolve.
 *
 * Use it everywhere an interaction matters for the funnel:
 *   trackEvent('gate_shown', { feature: 'holder-graph', requiredTier: 'watcher' })
 *   trackEvent('login_prompt_clicked', { source: 'gate' })
 *   trackEvent('subscribe_clicked', { tier: 'watcher' })
 *
 * Don't track things already implicit in an API call (the GET on
 * /api/token-safety/:mint is in request logs already).
 */

interface AnalyticsContext {
  isAuthenticated: boolean;
  userTier?: string;
  dnetTier?: string;
  dnetBalance?: number;
}
let authCtx: AnalyticsContext = { isAuthenticated: false };

/** Called by AuthProvider whenever the user object changes. */
export function setAnalyticsContext(ctx: AnalyticsContext): void {
  authCtx = ctx;
}

export function trackEvent(name: string, props: Record<string, unknown> = {}): void {
  try {
    // Caller-supplied props win over the auth-context defaults so an
    // override (e.g. requiredTier='deep_diver') still lands as-is.
    const merged: Record<string, unknown> = {
      isAuthenticated: authCtx.isAuthenticated,
      ...(authCtx.userTier !== undefined ? { userTier: authCtx.userTier } : {}),
      ...(authCtx.dnetTier !== undefined ? { dnetTier: authCtx.dnetTier } : {}),
      ...(authCtx.dnetBalance !== undefined ? { dnetBalance: authCtx.dnetBalance } : {}),
      ...props,
    };
    apiFetch(`${getApiHost()}/api/analytics/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, props: merged, ts: Date.now() }),
      keepalive: true, // survives navigation, same guarantee as sendBeacon
    }).catch(() => { /* analytics never breaks the UI */ });
    // Dual-write to PostHog for funnel/retention/session-replay views.
    // No-op when REACT_APP_POSTHOG_KEY isn't set. Sanitization (drop
    // walletAddress etc.) happens inside posthogCapture so server-side
    // analytics_events keeps the full payload either way.
    posthogCapture(name, merged);
  } catch {
    /* swallow */
  }
}

/**
 * Report a caught error for diagnostics. Fires two ways so it's visible
 * regardless of how you're looking: a rich PostHog $exception (stack + grouping,
 * for Error Tracking) AND a first-class `client_error` funnel event (server
 * analytics_events + PostHog), so failures show up right next to the funnel.
 *
 * Use in catch blocks that would otherwise swallow the error into local UI
 * state — especially checkout/payment flows:
 *   trackError(err, { flow: 'fiat_credit_checkout', bundle: 10 })
 */
export function trackError(error: unknown, context: Record<string, unknown> = {}): void {
  try {
    const message = error instanceof Error ? error.message : String(error);
    captureError(error, context);
    trackEvent('client_error', { ...context, message });
  } catch {
    /* errors in error-reporting must never break the UI */
  }
}
